/**
 * Constants for Email Flow Configuration Dialog
 */

// View Popup Types
export const VIEW_POPUP_TYPES = {
  EMAIL_CHIPS: "emailChips",
  CHIPS: "Smart Chips",
  ACTIVE_MEMBERS: "Active Members",
  FUNCTIONS: "Functions",
};

// Editor Keys (mirror SmartChip: ~ chips, @ users, # task id — here # maps to Functions)
export const EDITOR_KEYS = {
  AT_SYMBOL: "@",
  TILDE: "~",
  HASH: "#",
};

/** Maps inserted trigger character to EmailChipsPopup section */
export const TRIGGER_KEY_TO_SECTION = {
  [EDITOR_KEYS.TILDE]: VIEW_POPUP_TYPES.CHIPS,
  [EDITOR_KEYS.AT_SYMBOL]: VIEW_POPUP_TYPES.ACTIVE_MEMBERS,
  [EDITOR_KEYS.HASH]: VIEW_POPUP_TYPES.FUNCTIONS,
};

// View Popup Initial State
export const INITIAL_VIEW_POPUP_STATE = {
  ref: null,
  visible: false,
  type: "",
  tempId: null,
  defaultSection: null,
};

// Task Variables Configuration
export const TASK_VARIABLES = [
  {
    GROUP: "Task Chips",
    GROUP_TYPE: "taskvariables",
    name: "Task Id",
    textInfo: "Task | Id",
    chipId: "taskvariables_TaskId_no-id",
  },
  {
    GROUP: "Task Chips",
    GROUP_TYPE: "taskvariables",
    name: "Task Name",
    textInfo: "Task | Name",
    chipId: "taskvariables_TaskName_no-id",
  },
  {
    GROUP: "Task Chips",
    GROUP_TYPE: "taskvariables",
    name: "Task Due Date",
    textInfo: "Task | Due Date",
    chipId: "taskvariables_TaskDueDate_no-id",
  },
  {
    GROUP: "Task Chips",
    GROUP_TYPE: "taskvariables",
    name: "Task Priority",
    textInfo: "Task | Priority",
    chipId: "taskvariables_TaskPriority_no-id",
  }
];

// Email Chips Configuration
export const EMAIL_CHIPS = [
  {
    GROUP: "Email Chips",
    GROUP_TYPE: "emailchips",
    name: "Sender Email",
    textInfo: "Email | Sender Email",
    chipId: "emailchips_SenderEmail_no-id",
  },
  {
    GROUP: "Email Chips",
    GROUP_TYPE: "emailchips",
    name: "Sender Name",
    textInfo: "Email | Sender Name",
    chipId: "emailchips_SenderName_no-id",
  },
  {
    GROUP: "Email Chips",
    GROUP_TYPE: "emailchips",
    name: " Reference Id (Ticket Id)",
    textInfo: "Email | Reference Id",
    chipId: "emailchips_ReferenceId_no-id",
  },
  {
    GROUP: "Email Chips",
    GROUP_TYPE: "emailchips",
    name: "Unique Id",
    textInfo: "Email Variable | Unique Id",
    chipId: "emailchips_UniqueId_no-id",
  },
];


/** Date / time function chips for the Functions tab of the email chips popup */
export const EMAIL_FUNCTION_CHIPS = [
  {
    label: "Date",
    value: "Today",
    textInfo: "Function | Today",
    chipId: "timevariable_Today_no-id",
  },
  {
    label: "Time",
    value: "Now",
    textInfo: "Function | Now",
    chipId: "timevariable_Now_no-id",
  },
];

/**
 * Get available fields for email chips popup
 * @param {Array} workflowCustomVariables - Custom workflow variables
 * @param {Array} userProfileFields - User profile fields
 * @returns {Array} Combined array of available fields
 */
export const getAvailableFields = (workflowCustomVariables = [], userProfileFields = []) => [
  ...workflowCustomVariables?.map((wcv) => ({
    ...wcv,
    GROUP: "Workflow Chips",
    GROUP_TYPE: "formsmartchips",
    textInfo: `Form | ${wcv.name}`,
    chipId: `formsmartchips_${wcv.name.replace(/\s+/g, "")}_${wcv.id || wcv._id || "no-id"}`,
  })),
  ...userProfileFields?.map((acv) => ({
    ...acv,
    GROUP: "Creator Chips",
    GROUP_TYPE: "creatorchips",
    textInfo: `Creator | ${acv.name}`,
    chipId: `creatorchips_${acv?.name?.replace(/\s+/g, "")}_${acv.id || acv._id || "no-id"}`,
  })),
  ...userProfileFields?.map((acv) => ({
    ...acv,
    GROUP: "Assignee Chips",
    GROUP_TYPE: "assigneechips",
    textInfo: `Assignee | ${acv.name}`,
    chipId: `assigneechips_${acv?.name?.replace(/\s+/g, "")}_${acv.id || acv._id || "no-id"}`,
  })),
  ...TASK_VARIABLES,
  ...EMAIL_CHIPS,
];

export const EMAIL_FLOW_ATTACHMENT_OPTIONS = [
  { key: "description", label: "Description", defaultSelected: true, enabled: true },
  { key: "approvals", label: "Approval", defaultSelected: false, enabled: true },
  { key: "forms", label: "Forms", defaultSelected: false, enabled: true },
  { key: "attachments", label: "Attachments", defaultSelected: false, enabled: false },
  { key: "emails", label: "Emails", defaultSelected: false, enabled: false },
  { key: "activityLogs", label: "Activity Logs", defaultSelected: false, enabled: false },
  { key: "comments", label: "Comments", defaultSelected: false, enabled: false },
  { key: "linkedTasks", label: "Linked Tasks", defaultSelected: false, enabled: false },
  { key: "tags", label: "Tags", defaultSelected: false, enabled: false },
  { key: "followers", label: "Followers", defaultSelected: false, enabled: false },
];

export const TASK_PDF_TYPE = "taskpdf";
/** Number of attachment options shown before "more..." expands the list */
export const EMAIL_FLOW_ATTACHMENT_INITIAL_VISIBLE_COUNT = 4;

/** Default PDF file name for generated task attachments */
export const EMAIL_FLOW_ATTACHMENT_DEFAULT_FILE_NAME = "Task.pdf";

/** Labels and copy for the Attachment modal (EmailFlowConfigDialog) */
export const EMAIL_FLOW_ATTACHMENT_UI = {
  SECTIONS_LABEL: "Sections to Attach",
  FILE_NAME_LABEL: "File name",
  SELECT_ALL: "Select All",
  MORE: "more...",
  SAVE: "Save",
  CANCEL: "Cancel",
  REMOVE_ATTACHMENT_CONFIRM: "Remove this attachment?",
  CONFIRM_REMOVE_TITLE: "Confirm",
  CANCEL_REMOVE_TITLE: "Cancel",
};

/** @type {Record<string, { key: string, label: string }>} */
const ATTACHMENT_OPTION_BY_KEY = Object.fromEntries(EMAIL_FLOW_ATTACHMENT_OPTIONS.map((o) => [o.key, { key: o.key, label: o.label }]));
const VALID_ATTACHMENT_KEYS = new Set(Object.keys(ATTACHMENT_OPTION_BY_KEY));

/**
 * Order selected keys to match EMAIL_FLOW_ATTACHMENT_OPTIONS order.
 * @param {string[]} keys - Selected option keys
 * @returns {string[]}
 * @author Abhishek Dhillon
 */
export const orderAttachmentKeys = (keys) => EMAIL_FLOW_ATTACHMENT_OPTIONS.map((o) => o.key).filter((k) => keys?.includes(k));

/**
 * Convert ordered keys to includedFields entries (key + label from options).
 * @param {string[]} keys
 * @returns {{ key: string, label: string }[]}
 * @author Abhishek Dhillon
 */
export const keysToAllowedTypes = (keys) =>
  orderAttachmentKeys(keys).map((k) => ATTACHMENT_OPTION_BY_KEY[k] || { key: k, label: k });

/**
 * Extract ordered keys from includedFields.
 * @param {{ key: string, label?: string }[]|undefined|null} includedFields
 * @returns {string[]}
 * @author Abhishek Dhillon
 */
export const getKeysFromAllowedTypes = (includedFields) =>
  orderAttachmentKeys(
    (includedFields || []).map((t) => t?.key).filter((k) => typeof k === "string" && VALID_ATTACHMENT_KEYS.has(k))
  );

/**
 * Normalize includedFields: canonical order, unique keys, label from options or preserved from payload.
 * @param {{ key: string, label?: string }[]|undefined|null} includedFields
 * @returns {{ key: string, label: string }[]}
 * @author Abhishek Dhillon
 */
export const normalizeAllowedTypes = (includedFields) => {
  const raw = includedFields || [];
  const keys = orderAttachmentKeys(raw.map((t) => t?.key).filter((k) => typeof k === "string" && VALID_ATTACHMENT_KEYS.has(k)));
  return keys.map((k) => {
    const fromPayload = raw.find((t) => t?.key === k);
    const fromOption = ATTACHMENT_OPTION_BY_KEY[k];
    return { key: k, label: fromPayload?.label ?? fromOption?.label ?? k };
  });
};

/**
 * Default draft for the attachment modal when none is saved yet (not persisted until user saves).
 * @returns {{ name: string, includedFields: { key: string, label: string }[] }}
 * @author Abhishek Dhillon
 */
export const getDefaultAttachmentDraft = () => {
  const defaultAllowed = keysToAllowedTypes(EMAIL_FLOW_ATTACHMENT_OPTIONS.filter((o) => o.defaultSelected).map((o) => o.key));
  return { name: EMAIL_FLOW_ATTACHMENT_DEFAULT_FILE_NAME, includedFields: defaultAllowed, type: TASK_PDF_TYPE };
};

/**
 * Build attachments array from email template data. Empty API array stays empty (no injected defaults).
 * At most one attachment object is returned (UI allows a single entry).
 * @param {Object|null|undefined} data - Full email template
 * @returns {{ name: string, includedFields: { key: string, label: string }[] }[]}
 * @author Abhishek Dhillon
 */
export const buildInitialAttachments = (data) => {
  const defaultName = EMAIL_FLOW_ATTACHMENT_DEFAULT_FILE_NAME;
  const defaultAllowed = keysToAllowedTypes(EMAIL_FLOW_ATTACHMENT_OPTIONS.filter((o) => o.defaultSelected).map((o) => o.key));

  if (!data) {
    return [];
  }

  const att = data.attachments;

  if (Array.isArray(att)) {
    if (att.length === 0) {
      return [];
    }
    if (att.every((x) => typeof x === "string")) {
      const keys = orderAttachmentKeys(att.filter((k) => typeof k === "string" && VALID_ATTACHMENT_KEYS.has(k)));
      return [{ name: defaultName, type: TASK_PDF_TYPE, includedFields: keysToAllowedTypes(keys) }];
    }
    const items = att
      .filter((item) => item && typeof item === "object" && !Array.isArray(item))
      .map((item) => ({
        name: typeof item.name === "string" && item.name.trim() ? item.name.trim() : defaultName,
        type: TASK_PDF_TYPE,
        includedFields: Array.isArray(item?.includedFields) ? normalizeAllowedTypes(item?.includedFields) : defaultAllowed,
      }));
    return items.length ? items.slice(0, 1) : [];
  }

  return [];
};

// Export all constants
export default {
  VIEW_POPUP_TYPES,
  EDITOR_KEYS,
  TRIGGER_KEY_TO_SECTION,
  INITIAL_VIEW_POPUP_STATE,
  TASK_VARIABLES,
  EMAIL_CHIPS,
  EMAIL_FUNCTION_CHIPS,
  getAvailableFields,
  EMAIL_FLOW_ATTACHMENT_OPTIONS,
  EMAIL_FLOW_ATTACHMENT_UI,
  EMAIL_FLOW_ATTACHMENT_DEFAULT_FILE_NAME,
  buildInitialAttachments,
  getDefaultAttachmentDraft,
  orderAttachmentKeys,
  keysToAllowedTypes,
  getKeysFromAllowedTypes,
  normalizeAllowedTypes,
};

